import React, { useState } from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { MapPin } from 'lucide-react';
import indiaTopo from '../data/india-topo.json';

// Target states we serve — matched to the `st_nm` field in the TopoJSON.
const SERVED = {
  Karnataka: { coords: [77.59, 12.97], city: 'Bengaluru · Mangalore' },
  Goa: { coords: [73.83, 15.49], city: 'Panaji' },
  Maharashtra: { coords: [72.87, 19.07], city: 'Mumbai' },
  'Tamil Nadu': { coords: [80.27, 13.08], city: 'Chennai' },
  Telangana: { coords: [78.48, 17.38], city: 'Hyderabad' },
};

export default function IndiaMap({ states = [] }) {
  const [active, setActive] = useState(null);

  return (
    <div className="relative w-full">
      <div className="relative aspect-[5/6] max-w-[520px] mx-auto">
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{ scale: 750, center: [82, 22] }}
          width={500}
          height={600}
          style={{ width: '100%', height: '100%' }}
        >
          <Geographies geography={indiaTopo}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const name = geo.properties?.st_nm;
                const isServed = !!SERVED[name] && states.includes(name);
                const isActive = active === name;
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseEnter={() => isServed && setActive(name)}
                    onMouseLeave={() => setActive(null)}
                    style={{
                      default: {
                        fill: isServed ? (isActive ? '#FDE68A' : '#FEF3C7') : '#F1F5F9',
                        stroke: '#CBD5E1',
                        strokeWidth: 0.6,
                        outline: 'none',
                        transition: 'fill 200ms',
                      },
                      hover: {
                        fill: isServed ? '#FDE68A' : '#E2E8F0',
                        stroke: '#94A3B8',
                        strokeWidth: 0.8,
                        outline: 'none',
                        cursor: isServed ? 'pointer' : 'default',
                      },
                      pressed: {
                        fill: isServed ? '#F5B800' : '#E2E8F0',
                        outline: 'none',
                      },
                    }}
                  />
                );
              })
            }
          </Geographies>

          {states.map((s) => {
            const p = SERVED[s];
            if (!p) return null;
            const isActive = active === s;
            return (
              <Marker
                key={s}
                coordinates={p.coords}
                onMouseEnter={() => setActive(s)}
                onMouseLeave={() => setActive(null)}
                style={{ default: { cursor: 'pointer' } }}
              >
                {/* Pulse ring */}
                <circle r={isActive ? 10 : 8} fill="#F5B800" fillOpacity="0.28">
                  <animate attributeName="r" values="5;10;5" dur="2.4s" repeatCount="indefinite" />
                  <animate attributeName="fill-opacity" values="0.35;0;0.35" dur="2.4s" repeatCount="indefinite" />
                </circle>
                {/* Main dot */}
                <circle r={isActive ? 4.5 : 3.5} fill="#F5B800" stroke="#0A2540" strokeWidth={1} />
                {isActive && (
                  <g>
                    <rect
                      x={8}
                      y={-22}
                      width={Math.max(s.length * 6.4 + 20, 110)}
                      height={34}
                      rx={5}
                      fill="#0A2540"
                    />
                    <text x={16} y={-10} fontSize="10" fontWeight="700" fill="#F5B800">
                      {s}
                    </text>
                    <text x={16} y={2} fontSize="8" fill="white" opacity="0.85">
                      {p.city}
                    </text>
                  </g>
                )}
              </Marker>
            );
          })}
        </ComposableMap>
      </div>

      {/* State legend below */}
      <div className="mt-6 flex flex-wrap justify-center gap-2">
        {states.map((s) => (
          <button
            key={s}
            onMouseEnter={() => setActive(s)}
            onMouseLeave={() => setActive(null)}
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
              active === s
                ? 'bg-[#F5B800] border-[#F5B800] text-[#0A2540]'
                : 'bg-white border-slate-200 text-slate-700 hover:border-[#F5B800]'
            }`}
          >
            <MapPin size={12} /> {s}
          </button>
        ))}
      </div>
    </div>
  );
}
